import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Card,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useCallback, useState, useContext, memo, useEffect } from 'react';

import axios, { AxiosError } from 'axios';
import moment from 'moment';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import constants from '../config/constants';
import { AlertContext } from '../context/alert.context';
import { useLanguage } from '../context/language.context';
import { IExpert, IPrivateService, ProfileData } from '../types';
import { getLocalizedErrorMessage } from '../utils/functions';
import FormButton from './common/FormButton';
import PrivateServicesTable from './PrivateServicesTable';
import useFetch from '../hooks/useFetch';

const ExpertProfile = memo(({ profileData }: { profileData?: IExpert }) => {
  const { t, dir } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingTitle, setIsSubmittingTitle] = useState(false);
  const [isSubmittingDescription, setIsSubmittingDescription] = useState(false);
  const [isSubmittingZoom, setIsSubmittingZoom] = useState(false);
  const [isSubmittingLocation, setIsSubmittingLocation] = useState(false);
  const [isSubmittingBank, setIsSubmittingBank] = useState(false);
  const [isSubmittingLicensedDealer, setIsSubmittingLicensedDealer] = useState(false);
  const [showSectionSaveButtons, setShowSectionSaveButtons] = useState(true);
  const [titleDialogOpen, setTitleDialogOpen] = useState(false);
  const [savedTitleData, setSavedTitleData] = useState<{ title?: string; english_title?: string } | null>(null);
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [savedDescriptionData, setSavedDescriptionData] = useState<{ he?: string; en?: string } | null>(null);
  const [zoomDialogOpen, setZoomDialogOpen] = useState(false);
  const [savedZoomData, setSavedZoomData] = useState<{ zoom?: string; zoom_password?: string } | null>(null);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [savedLocationData, setSavedLocationData] = useState<{ location?: string } | null>(null);
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [savedBankData, setSavedBankData] = useState<{ account_number?: string; bank_name?: string; branch_number?: string } | null>(null);
  
  // מערך של ID-ים של מומחים שיכולים לראות כפתורי שמירה בקטעים
  const allowedExpertIds = ['690765d0d859e4c8e2c77960','62f4da77454b14d740800a37'];
  
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    getValues,
    formState: { isDirty, errors },
  } = useForm<ProfileData>({
    defaultValues: profileData,
    mode: 'onChange',
  });

  useEffect(() => {
    if (profileData) {
      reset(profileData);
    }
  }, [profileData, reset]);

  // useEffect(() => {
  //   if (profileData?._id) {
  //     setShowSectionSaveButtons(allowedExpertIds.includes(profileData._id));
  //   } else {
  //     setShowSectionSaveButtons(false);
  //   }
  // }, [profileData?._id]);

  const privateServices = useFetch<IPrivateService[]>(`${constants.API_URL}/instructor/admin/private_services`);
  const alert = useContext(AlertContext);

  const updateProfile: SubmitHandler<ProfileData> = useCallback(
    async (data) => {
      setIsSubmitting(true);
      try {
        // Format insuranceExpiryDate to ISO format with timezone
      

        const formattedData = {
          ...data,
        
          insuranceExpiryDate: data.insuranceExpiryDate
            ? moment(data.insuranceExpiryDate).utc().startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS') + '+00:00'
            : data.insuranceExpiryDate,
        };
        await axios.put(`${constants.API_URL}/instructor/admin/profile`, formattedData);
        alert?.showAlert(t('profileUpdated'), 'success');
        reset(data); // Reset form with current values to clear dirty state
      } catch (error: any) {
        if (error instanceof AxiosError) {
          alert?.showAlert(
            getLocalizedErrorMessage(error, 'he') || t('somethingWentWrong'),
            'error'
          );
        } else alert?.showAlert(t('somethingWentWrong'), 'error');
      } finally {
        setIsSubmitting(false);
      }
    },
    [alert, reset]
  );

  const updateTitle = useCallback(async () => {
    setIsSubmittingTitle(true);
    try {
      const values = getValues();
      const response = await axios.put(`${constants.API_URL}/instructor/admin/profile-title`, {
        title: values.title,
        english_title: values.english_title,
      });
      alert?.showAlert(t('titleUpdated'), 'success');
      reset(values); // Reset form with current values to clear dirty state
      
      // הצגת פופ-אפ עם הערכים החדשים
      if (response.data) {
        setSavedTitleData({
          title: response.data.title,
          english_title: response.data.english_title,
        });
        setTitleDialogOpen(true);
      }
    } catch (error: any) {
      if (error instanceof AxiosError) {
        alert?.showAlert(
          getLocalizedErrorMessage(error, 'he') || t('somethingWentWrong'),
          'error'
        );
      } else alert?.showAlert(t('somethingWentWrong'), 'error');
    } finally {
      setIsSubmittingTitle(false);
    }
  }, [alert, reset, getValues]);

  const updateDescription = useCallback(async () => {
    setIsSubmittingDescription(true);
    try {
      const values = getValues();
      const response = await axios.put(`${constants.API_URL}/instructor/admin/profile-description`, {
        description_lng: {
          he: values.description_lng?.he,
          en: values.description_lng?.en,
        },
      });
      alert?.showAlert(t('descriptionUpdated'), 'success');
      reset(values); // Reset form with current values to clear dirty state
      
      // הצגת פופ-אפ עם הערכים החדשים
      if (response.data?.description_lng) {
        setSavedDescriptionData({
          he: response.data.description_lng.he,
          en: response.data.description_lng.en,
        });
        setDescriptionDialogOpen(true);
      }
    } catch (error: any) {
      if (error instanceof AxiosError) {
        alert?.showAlert(
          getLocalizedErrorMessage(error, 'he') || t('somethingWentWrong'),
          'error'
        );
      } else alert?.showAlert(t('somethingWentWrong'), 'error');
    } finally {
      setIsSubmittingDescription(false);
    }
  }, [alert, reset, getValues]);

  const updateZoom = useCallback(async () => {
    setIsSubmittingZoom(true);
    try {
      const values = getValues();
      const response = await axios.put(`${constants.API_URL}/instructor/admin/profile-zoom`, {
        zoom: values.zoom,
        zoom_password: values.zoom_password,
      });
      alert?.showAlert(t('zoomUpdated'), 'success');
      reset(values); // Reset form with current values to clear dirty state
      
      // הצגת פופ-אפ עם הערכים החדשים
      if (response.data) {
        setSavedZoomData({
          zoom: response.data.zoom,
          zoom_password: response.data.zoom_password,
        });
        setZoomDialogOpen(true);
      }
    } catch (error: any) {
      if (error instanceof AxiosError) {
        alert?.showAlert(
          getLocalizedErrorMessage(error, 'he') || t('somethingWentWrong'),
          'error'
        );
      } else alert?.showAlert(t('somethingWentWrong'), 'error');
    } finally {
      setIsSubmittingZoom(false);
    }
  }, [alert, reset, getValues]);

  const updateLocation = useCallback(async () => {
    setIsSubmittingLocation(true);
    try {
      const values = getValues();
      const response = await axios.put(`${constants.API_URL}/instructor/admin/profile-location`, {
        location: values.location,
      });
      alert?.showAlert(t('locationUpdated'), 'success');
      reset(values); // Reset form with current values to clear dirty state
      
      // הצגת פופ-אפ עם הערך החדש
      if (response.data) {
        setSavedLocationData({
          location: response.data.location,
        });
        setLocationDialogOpen(true);
      }
    } catch (error: any) {
      if (error instanceof AxiosError) {
        alert?.showAlert(
          getLocalizedErrorMessage(error, 'he') || t('somethingWentWrong'),
          'error'
        );
      } else alert?.showAlert(t('somethingWentWrong'), 'error');
    } finally {
      setIsSubmittingLocation(false);
    }
  }, [alert, reset, getValues]);

  const updateBank = useCallback(async () => {
    setIsSubmittingBank(true);
    try {
      const values = getValues();
      const response = await axios.put(`${constants.API_URL}/instructor/admin/profile-bank-details`, {
        account_number: values.account_number,
        bank_name: values.bank_name,
        branch_number: values.branch_number,
      });
      alert?.showAlert(t('bankUpdated'), 'success');
      reset(values); // Reset form with current values to clear dirty state
      
      // הצגת פופ-אפ עם הערכים החדשים
      if (response.data) {
        setSavedBankData({
          account_number: response.data.account_number,
          bank_name: response.data.bank_name,
          branch_number: response.data.branch_number,
        });
        setBankDialogOpen(true);
      }
    } catch (error: any) {
      if (error instanceof AxiosError) {
        alert?.showAlert(
          getLocalizedErrorMessage(error, 'he') || t('somethingWentWrong'),
          'error'
        );
      } else alert?.showAlert(t('somethingWentWrong'), 'error');
    } finally {
      setIsSubmittingBank(false);
    }
  }, [alert, reset, getValues]);

  const updateLicensedDealer = useCallback(async (value: boolean) => {
    setIsSubmittingLicensedDealer(true);
    try {
      await axios.put(`${constants.API_URL}/instructor/admin/profile-licensed-dealer`, {
        licensed_dealer: value,
      });
      alert?.showAlert(t('vatStatusUpdated'), 'success');
    } catch (error: any) {
      if (error instanceof AxiosError) {
        alert?.showAlert(
          getLocalizedErrorMessage(error, 'he') || t('somethingWentWrong'),
          'error'
        );
      } else alert?.showAlert(t('somethingWentWrong'), 'error');
    } finally {
      setIsSubmittingLicensedDealer(false);
    }
  }, [alert]);

  return (
    <div>
      <Table sx={{ direction: dir, textAlign: 'right' }}>

        
        <TableHead>
          <TableRow>
            <TableCell width={100} />
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />
          </TableRow>
        </TableHead>


        <TableBody style={{ textAlign: 'right' }}>


        <TableRow style={{ direction: dir, textAlign: 'right' }}>
            <TableCell>{t('myProfileLink')}</TableCell>

            <TableCell>
              <a style={{textDecoration: 'underline', color: 'inherit'}} href={`https://app.grouphug.online/expert/${profileData?._id}`} target='_blank' rel='noreferrer'>{profileData?.name}</a>
            </TableCell>
            <TableCell>
              
            </TableCell>
          </TableRow>
       
          <TableRow style={{ direction: dir, textAlign: 'right' }}>
            <TableCell>{t('name')}</TableCell>

            <TableCell>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField {...field} placeholder={t('name')} disabled />
                )}
              />
            </TableCell>
            <TableCell>
              <Controller
                name="english_name"
                control={control}
                disabled={true}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder={t('nameEnglish')}
                    inputProps={{ dir: 'ltr' }}
                  />
                )}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t('vatStatus')}</TableCell>
            <TableCell>
              <Controller
                control={control}
                name="licensed_dealer"
                render={({ field }) => (
                  <FormControl disabled={isSubmittingLicensedDealer}>
                   <RadioGroup
                      row
                      aria-labelledby="demo-controlled-radio-buttons-group"
                      value={String(field.value)}
                      onChange={(e) => {
                        const newValue = e.target.value === 'true';
                        field.onChange(newValue);
                        updateLicensedDealer(newValue);
                      }}
                    >
                      <FormControlLabel
                        value="true"
                        control={<Radio />}
                        label={t('authorizedDealer')}
                      />
                      <FormControlLabel
                        value="false"
                        control={<Radio />}
                        label={t('exemptDealer')}
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t('contact')}</TableCell>
            <TableCell>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField {...field} placeholder={t('emailPlaceholder')} disabled fullWidth />
                )}
              />
            </TableCell>

            <TableCell>
              <Controller
                name="phone"
                disabled={true}
                control={control}
                rules={{ required: true }}
                render={({ field, formState }) => (
                  <TextField
                    {...field}
                    error={Boolean(formState.errors[field.name])}
                    placeholder={t('phonePlaceholder')}
                  />
                )}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={5}>
              <Card dir={dir} style={{ marginTop: 20, width: '100%'}}>
                <CardHeader title={t('titleLabel')} />
                <Box p={2} display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <Controller
                      rules={{ required: true }}
                      name="title"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder={t('titleLabel')}
                          fullWidth
                          helperText={`${field.value?.length ?? 0}/60`}
                        />
                      )}
                    />
                  </Box>
                  <Box>
                    <Controller
                      name="english_title"
                      rules={{ required: true }}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder={t('titleEnglish')}
                          fullWidth
                          helperText={`${field.value?.length ?? 0}/60`}
                          inputProps={{
                            style: { textAlign: 'left', direction: 'ltr' },
                          }}
                        />
                      )}
                    />
                  </Box>
                  {showSectionSaveButtons && (
                    <Box display="flex" justifyContent="flex-end" pt={1}>
                      <FormButton
                        isSubmitting={isSubmittingTitle}
                        isDirty={true}
                        label={t('save')}
                        onClick={updateTitle}
                      />
                    </Box>
                  )}
                </Box>
              </Card>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={5}>
              <Card dir={dir} style={{ marginTop: 20, width: '100%'}}>
                <CardHeader title={t('descriptionLabel')} />
                <Box p={2} display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <Controller
                      name="description_lng.he"
                      rules={{ required: false }}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder={t('descriptionLabel')}
                          fullWidth
                          multiline
                          helperText={`${field.value?.length ?? 0}/1200`}
                        />
                      )}
                    />
                  </Box>
                  <Box>
                    <Controller
                      name="description_lng.en"
                      rules={{ required: false }}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder={t('descriptionEnglish')}
                          inputProps={{
                            style: { textAlign: 'left', direction: 'ltr' },
                          }}
                          fullWidth
                          multiline
                          helperText={`${field.value?.length ?? 0}/1200`}
                        />
                      )}
                    />
                  </Box>
                  {showSectionSaveButtons && (
                    <Box display="flex" justifyContent="flex-end" pt={1}>
                      <FormButton
                        isSubmitting={isSubmittingDescription}
                        isDirty={true}
                        label={t('save')}
                        onClick={updateDescription}
                      />
                    </Box>
                  )}
                </Box>
              </Card>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell colSpan={5}>
              <Card dir={dir} style={{ marginTop: 20, width: '100%'}}>
                <CardHeader title={t('zoom')} />
                <Box p={2} display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <Controller
                      name="zoom"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder={t('zoomLink')}
                          fullWidth
                          type="url"
                        />
                      )}
                    />
                  </Box>
                  <Box>
                    <Controller
                      name="zoom_password"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} placeholder={t('zoomPassword')} fullWidth />
                      )}
                    />
                  </Box>
                  {showSectionSaveButtons && (
                    <Box display="flex" justifyContent="flex-end" pt={1}>
                      <FormButton
                        isSubmitting={isSubmittingZoom}
                        isDirty={true}
                        label={t('save')}
                        onClick={updateZoom}
                      />
                    </Box>
                  )}
                </Box>
              </Card>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell colSpan={5}>
              <Card dir={dir} style={{ marginTop: 20, width: '100%'}}>
                <CardHeader title={t('clinicCity')} />
                <Box p={2} display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" gap={2} flexDirection="row" flexWrap="wrap">
                    <Box flex={1} minWidth={200}>
                      <Typography variant="body2" sx={{ mb: 1 }}>{t('clinicCityNote')}</Typography>
                      <Controller
                        name="location"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            placeholder=""
                            fullWidth
                            rows={3}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                  {showSectionSaveButtons && (
                    <Box display="flex" justifyContent="flex-end" pt={1}>
                      <FormButton
                        isSubmitting={isSubmittingLocation}
                        isDirty={true}
                        label={t('save')}
                        onClick={updateLocation}
                      />
                    </Box>
                  )}
                </Box>
              </Card>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell colSpan={5}>
              <Card dir={dir} style={{ marginTop: 20, width: '100%'}}>
                <CardHeader title={t('bankDetails')} />
                <Box p={2} display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" gap={2} flexDirection="row" flexWrap="wrap">
                    <Box flex={1} minWidth={200}>
                      <Typography variant="body2" sx={{ mb: 1 }}>{t('accountNumber')}</Typography>
                      <Controller
                        name="account_number"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            placeholder={t('accountNumber')}
                            fullWidth
                          />
                        )}
                      />
                    </Box>
                    <Box flex={1} minWidth={200}>
                      <Typography variant="body2" sx={{ mb: 1 }}>{t('bankName')}</Typography>
                      <Controller
                        name="bank_name"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            placeholder={t('bankName')}
                            fullWidth
                          />
                        )}
                      />
                    </Box>
                    <Box flex={1} minWidth={200}>
                      <Typography variant="body2" sx={{ mb: 1 }}>{t('branchNumber')}</Typography>
                      <Controller
                        name="branch_number"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            placeholder={t('branchNumber')}
                            fullWidth
                          />
                        )}
                      />
                    </Box>
                  </Box>
                  {showSectionSaveButtons && (
                    <Box display="flex" justifyContent="flex-end" pt={1}>
                      <FormButton
                        isSubmitting={isSubmittingBank}
                        isDirty={true}
                        label={t('save')}
                        onClick={updateBank}
                      />
                    </Box>
                  )}
                </Box>
              </Card>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell colSpan={5}>
              <Card dir={dir} style={{ marginTop: 20, width: '100%'}}>
                <CardHeader title={t('certificates')} />
                <Box p={2} display='flex' flexDirection='column' gap={2}>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: 'grey.50',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 1 }}>{t('insuranceCertificate')}</Typography>
                    <Box display='flex' gap={2} alignItems='center'>
                      {/* <Button
                        variant='outlined'
                        onClick={() => {
                          const el = document.getElementById('insuranceCertificateInput') as HTMLInputElement | null;
                          el?.click();
                        }}
                      >
                        העלאת קובץ
                      </Button>
                      <input
                        id='insuranceCertificateInput'
                        type='file'
                        accept='image/*,application/pdf'
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = () => {
                            const result = reader.result as string;
                            setValue('insuranceCertificate', result, { shouldDirty: true });
                            (async () => {
                              try {
                                await axios.post(`${constants.API_URL}/instructor/admin/expert-upload-insurance-certificate`, {
                                  insuranceCertificate: result,
                                });
                                alert?.showAlert('התעודה הועלתה בהצלחה', 'success');
                              } catch (error: any) {
                                if (error instanceof AxiosError) {
                                  alert?.showAlert(
                                    getLocalizedErrorMessage(error, 'he') || 'שגיאה בהעלאת התעודה',
                                    'error'
                                  );
                                } else {
                                  alert?.showAlert('שגיאה בהעלאת התעודה', 'error');
                                }
                              }
                            })();
                          };
                          reader.readAsDataURL(file);
                          e.currentTarget.value = '';
                        }}
                      /> */}
                      {/* {watch('insuranceCertificate') && (
                        <Typography variant='body2'>נבחר קובץ</Typography>
                      )} */}
                    </Box>
                    <Box mt={2}>
                      {(() => {
                        const value = (watch('insuranceCertificate') as string) || (profileData?.insuranceCertificate as string) || '';
                        if (!value) return <Typography variant="body2" color="text.secondary">{t('noInsurance')}</Typography>;
                        const lower = value.toLowerCase();
                        const isPdf = lower.startsWith('data:application/pdf') || lower.endsWith('.pdf');
                        if (isPdf) {
                          return (
                            <a
                              href={value}
                              target='_blank'
                              rel='noreferrer'
                              style={{ fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', color: '#1976d2' }}
                            >
                              {t('viewPDF')}
                            </a>
                          );
                        }
                        return (
                          <img
                            src={value}
                            alt='תעודה'
                            style={{ maxWidth: '300px', maxHeight: '300px', objectFit: 'contain' }}
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        );
                      })()}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: 'grey.50',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    
                     
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 1 }}>{t('insuranceExpiry')}</Typography>
                    <Controller
                      name="insuranceExpiryDate"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          type="date"
                          value={
                            field.value
                              ? moment(field.value).format('YYYY-MM-DD')
                              : ''
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value ? moment(value).toDate() : null);
                          }}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          disabled={true}
                          placeholder="תאריך תפוגת ביטוח"
                          sx={{ maxWidth: 240 }}
                        />
                      )}
                    />
                  </Box>

                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: 'grey.50',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 1 }}>תעודות מקצועיות</Typography>
                    {profileData?.professionalCertificates && profileData.professionalCertificates.length > 0 ? (
                      <Box display="flex" gap={2} flexWrap="wrap">
                        {profileData.professionalCertificates.map((cert, index) => (
                          <Box key={index}>
                            <img 
                              src={cert} 
                              alt={`תעודה מקצועית ${index + 1}`} 
                              style={{ maxWidth: '300px', maxHeight: '300px', objectFit: 'contain' }}
                            />
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">אין תעודות מקצועיות</Typography>
                    )}
                  </Box>
                </Box>
              </Card>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell colSpan={5}>
              <Card dir={dir} style={{ marginTop: 20, width: '100%'}}>
                  <CardHeader title={t('oneToOneSessionsLabel')} />
                  <PrivateServicesTable refetch={privateServices.refetch} privateServices={privateServices.data?.filter(service => !service.public && service.active) ?? []} seriesTermsConfirmed={profileData?.seriesTermsConfirmed ?? null} />
              </Card>
            </TableCell>
          </TableRow>

          
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={3}>
              <Box>
                <Typography variant="caption">{`עודכן לאחרונה: ${moment(
                  profileData?.updatedAt
                ).format('DD/MM/YY HH:mm')}`}</Typography>
              </Box>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
      {!showSectionSaveButtons && (
        <Box p={2} textAlign={'end'}>
          <FormButton
            isSubmitting={isSubmitting}
            isDirty={isDirty}
            label={t('save')}
            onClick={handleSubmit(updateProfile)}
          />
        </Box>
      )}
      <Box py={3}>
        <Typography textAlign="center" fontSize={14}>
          {'במידה ויש צורך בשינוי פרטים נוספים - אנא פנו אלינו למייל'}
          <Link href="mailto:experts@grouphug.online">
          experts@grouphug.online
          </Link>
        </Typography>
      </Box>
      
      <Dialog 
        open={titleDialogOpen} 
        onClose={() => setTitleDialogOpen(false)}
        dir="rtl"
      >
        <DialogTitle>הערכים החדשים שנשמרו</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                טייטל:
              </Typography>
              <Typography variant="body1">
                {savedTitleData?.title || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                טייטל באנגלית:
              </Typography>
              <Typography variant="body1" dir="ltr" textAlign="left">
                {savedTitleData?.english_title || '-'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTitleDialogOpen(false)} variant="contained">
            סגור
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog 
        open={descriptionDialogOpen} 
        onClose={() => setDescriptionDialogOpen(false)}
        dir="rtl"
      >
        <DialogTitle>הערכים החדשים שנשמרו</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                תיאור:
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {savedDescriptionData?.he || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                תיאור באנגלית:
              </Typography>
              <Typography variant="body1" dir="ltr" textAlign="left" sx={{ whiteSpace: 'pre-wrap' }}>
                {savedDescriptionData?.en || '-'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDescriptionDialogOpen(false)} variant="contained">
            סגור
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog 
        open={zoomDialogOpen} 
        onClose={() => setZoomDialogOpen(false)}
        dir="rtl"
      >
        <DialogTitle>הערכים החדשים שנשמרו</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                קישור קבוע:
              </Typography>
              <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                {savedZoomData?.zoom || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                סיסמה:
              </Typography>
              <Typography variant="body1">
                {savedZoomData?.zoom_password || '-'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZoomDialogOpen(false)} variant="contained">
            סגור
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog 
        open={locationDialogOpen} 
        onClose={() => setLocationDialogOpen(false)}
        dir="rtl"
      >
        <DialogTitle>הערך החדש שנשמר</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                עיר הקליניקה:
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {savedLocationData?.location || '-'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationDialogOpen(false)} variant="contained">
            סגור
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog 
        open={bankDialogOpen} 
        onClose={() => setBankDialogOpen(false)}
        dir="rtl"
      >
        <DialogTitle>הערכים החדשים שנשמרו</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                מספר חשבון:
              </Typography>
              <Typography variant="body1">
                {savedBankData?.account_number || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                שם בנק:
              </Typography>
              <Typography variant="body1">
                {savedBankData?.bank_name || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                מספר סניף:
              </Typography>
              <Typography variant="body1">
                {savedBankData?.branch_number || '-'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBankDialogOpen(false)} variant="contained">
            סגור
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
});

ExpertProfile.displayName = 'ExpertProfile';

export default ExpertProfile;
